import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthLayout } from '@features/auth/layouts/AuthLayout';
import { useForgotPassword } from '@features/auth/lib/useForgotPassword';
import { Button } from '@components/ui/button';
import { Form, FormField, FormItem, FormControl } from '@components/ui/form';
import { FloatingInput } from '@components/ui/input-floating';

const ForgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address.'),
});

type ForgotPasswordFormData = z.infer<typeof ForgotPasswordSchema>;

export const ForgotPasswordForm: React.FC = () => {
  const { forgotPassword, error, isLoading, success, clear } = useForgotPassword();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string>('');
  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: { email: '' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    await forgotPassword(data);

    if (!error) {
      setSubmittedEmail(data.email);
      form.reset({ email: '' });
      setHasSubmitted(true);
    }
  };

  const {
    formState: { errors },
    clearErrors,
    watch,
  } = form;

  const shouldShowSuccess = hasSubmitted && !error;
  const emailValue = watch('email');
  const isSubmitDisabled = isLoading;

  useEffect(() => {
    clear();
  }, [emailValue, clear]);

  return (
    <AuthLayout
      title={shouldShowSuccess ? "Reset link is on the way!" : "Well, that's embarrassing. Looks like someone forgot their password."}
      variant="reset"
      headerClassName="2xl:pt-21 max-md:pb-59"
      titleClassName="text-[40px] max-w-[86%] leading-[1.1] max-md:text-[28px]"
      contentClassName="2xl:pt-36.5 2xl:pl-23.5"
    >
      <div className="w-full max-w-[450px]">
        {shouldShowSuccess ? (
          <>
            <section className="mb-3">
              <h2 className="text-4xl font-extrabold leading-8 mb-2">
                Check your inbox!
              </h2>
              <p className="text-[14px] max-sm:max-w-87 flex-wrap max-w-[430px]">
                We've just sent you an email with a link to reset your password. It should arrive in a minute or two.
                <br />Don't see it? Check your spam or junk folder just in case. Still nothing? No worries—just click the resend link and we'll pop it off to you again.
              </p>
            </section>
            <div className="flex max-w-107.5">
              <Button
                type="button"
                onClick={() => onSubmit({ email: submittedEmail })}
                disabled={isSubmitDisabled}
                className="mt-10 bg-primary text-white text-[19px] font-bold py-2 h-14.5 rounded-full w-70 max-md:w-full disabled:text-[#ffffff55] disabled:opacity-100"
              >
                {isLoading ? 'Sending...' : 'Resend link'}
              </Button>
            </div>
            <p className="text-base xs:text-xl font-bold mt-10 text-center lg:text-left w-full max-w-[430px]">
              <Link
                to="/login"
                className="text-primary hover:underline whitespace-nowrap"
              >
                Return to login
              </Link>
            </p>
          </>
        ) : (
          <>
            <section className="mb-3">
              <h2 className="text-4xl font-bold leading-8 mb-2">
                Let's reset your password.
              </h2>
              <p className="text-[14px] max-sm:max-w-87 flex-wrap max-w-[430px]">
                Just enter the email associated with your account, and we'll send you a reset link.
              </p>
            </section>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="max-w-[430px]"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingInput
                          id="email"
                          label="Email address"
                          type="email"
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e);
                            clearErrors('email');
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          inputClass="w-full max-w-107.5 h-16 rounded-lg border border-[#B7B7B7] p-2"
                        />
                      </FormControl>
                      {fieldState.error && (
                        <p className="text-primary text-sm">
                          {fieldState.error.message}
                        </p>
                      )}
                    </FormItem>
                  )}
                />

                {error && <p className="text-primary text-sm">{error}</p>}
                {/* Remove standalone green success text per updated UX */}

                <div className="flex max-w-107.5">
                  <Button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className="mt-10 bg-primary text-white text-[19px] font-bold py-2 h-14.5 rounded-full w-70 max-md:w-full disabled:text-[#ffffff55] disabled:opacity-100"
                  >
                    {isLoading ? 'Submitting...' : 'SUBMIT'}
                  </Button>
                </div>

                <p className="text-base xs:text-xl font-extrabold mt-10 text-center lg:text-left w-full max-w-[430px]">
                  Returning user?{' '}
                  <Link
                    to="/login"
                    className="text-primary hover:underline whitespace-nowrap"
                  >
                    Click here to log in.
                  </Link>
                </p>
              </form>
            </Form>
          </>
        )}
      </div>
    </AuthLayout>
  );
};
